import { create, tsx, isVNode, v } from '@dojo/framework/core/vdom';
import block from '@dojo/framework/core/middleware/block';

import { HeroBackground } from '../../widgets/HeroBackground';
import { Section } from '../../widgets/Section';
import { ContentWrapper } from '../../widgets/ContentWrapper';
import getBlog from '../../blocks/wp-blog.block';
import getCategories from '../../blocks/wp-blog-category.block';
import getSeries from '../../blocks/wp-blog-series.block';
import BlogConnect from '../../widgets/BlogConnect';

import * as css from './Blog.m.css';
import Text from '../../widgets/Text';
import Link from '@dojo/framework/routing/Link';
import { DNode } from '@dojo/framework/core/interfaces';
import NewsLetter from '../newsletter/NewsLetter';

const twitterIcon = require('../../assets/images/icon-solid/social/footer-twitter-extra-small.svg');
const facebookIcon = require('../../assets/images/icon-solid/social/facebook-extra-small.svg');
const linkedInIcon = require('../../assets/images/icon-solid/social/footer-linkedin-extra-small.svg');
const rssIcon = require('../../assets/images/icon-solid/social/footer-rss-extra-small.svg');
const openGraphUrl = require('../../assets/images/misc/open-graph.png');

interface BlogProperties {
	slug: string;
}

const legacyImage = '2020/01/og_imageV1';

function getImageUrl(imageSource: string, stripHost = false) {
	if (imageSource.indexOf(legacyImage) !== -1) {
		return stripHost
			? `/${openGraphUrl.substr(openGraphUrl.lastIndexOf('/') + 1)}`
			: openGraphUrl;
	}
	return imageSource;
}

const factory = create({ block }).properties<BlogProperties>();

function processMeta(meta: DNode[]) {
	return meta.map((node) => {
		if (isVNode(node) && node.tag === 'meta') {
			if (/:image$/.test(node.properties.property)) {
				return v(
					node,
					{ ...node.properties, content: getImageUrl(node.properties.content, true) },
					node.children
				);
			}
		}
		return node;
	});
}

export const Blog = factory(function Blog({ properties, middleware: { block } }) {
	const { slug } = properties();

	const post = block(getBlog)(slug);
	const categories = block(getCategories)();
	const series = block(getSeries)();

	if (!post) {
		return null;
	}

	const [seriesId] = post.series;
	const seriesDetails = series?.find((series) => series.id === seriesId);
	const blogDate = new Date(post.date).toLocaleDateString('en-US', {
		month: 'long',
		day: 'numeric',
		year: 'numeric'
	});

	return (
		<div classes={[css.root]}>
			<head>{processMeta(post.meta)}</head>
			<HeroBackground background="level2BluePurple" />
			<Section>
				<ContentWrapper size="smaller">
					<article>
						<Text type="callout-head" as="h1">
							<span classes={[css.title]} innerHTML={post.title} />
						</Text>
						<div classes={[css.meta]}>
							<Text type="body" as="div">
								<span>{post.author}</span> |{' '}
								<span classes={[css.date]}>{blogDate}</span>
							</Text>
							<ul classes={[css.socials]}>
								<li classes={[css.socials]}>
									<a
										href="https://www.facebook.com/SitePen"
										target="_blank"
										rel="noopener"
										classes={[css.socialIcon]}
									>
										<img src={facebookIcon} alt="facebook" />
									</a>
								</li>
								<li classes={[css.socials]}>
									<a
										href="https://twitter.com/sitepen"
										target="_blank"
										rel="noopener"
										classes={[css.socialIcon]}
									>
										<img src={twitterIcon} alt="twitter" />
									</a>
								</li>
								<li classes={[css.socials]}>
									<a
										href="https://www.linkedin.com/company/sitepen-inc-"
										target="_blank"
										rel="noopener"
										classes={[css.socialIcon]}
									>
										<img src={linkedInIcon} alt="linkedin" />
									</a>
								</li>
								<li classes={[css.socials]}>
									<a href="/rss.xml" classes={[css.socialIcon]}>
										<img src={rssIcon} alt="rss feed" />
									</a>
								</li>
							</ul>
						</div>
						<img
							alt=""
							src={getImageUrl(post.image.source_url)}
							width={post.image.width}
							height={post.image.height}
							classes={[css.image]}
						/>
						<ul classes={[css.categories]}>
							{post.categories.map((id) => {
								const categoryDetails = categories?.find(
									(category) => category.id === id
								);
								if (categoryDetails) {
									const { name, id, slug } = categoryDetails;
									return (
										<li key={`category-${id}`} classes={css.category}>
											<Link to="category" params={{ slug }}>
												{name}
											</Link>
										</li>
									);
								}
							})}
						</ul>
						<div classes={['post-content']} innerHTML={post.content}></div>
					</article>
				</ContentWrapper>
			</Section>
			<Section shaded>
				<ContentWrapper>
					<BlogConnect slug={post.slug} />
				</ContentWrapper>
			</Section>
			{seriesDetails && (
				<Section>
					<ContentWrapper>
						<iframe
							id={`series-${seriesDetails.slug}`}
							src={`/preview/series/${seriesDetails.slug}`}
							classes={[css.series]}
							data-spacing="8"
						/>
					</ContentWrapper>
				</Section>
			)}
			{!seriesDetails && (
				<Section type="trailing">
					<ContentWrapper>
						<iframe
							title="Latest insights"
							id="latest-insights"
							src="preview/latest-insights"
							classes={[css.recentPosts]}
							data-spacing="8"
						/>
					</ContentWrapper>
				</Section>
			)}
			<Section type="trailing" shaded>
				<ContentWrapper>
					<NewsLetter />
				</ContentWrapper>
			</Section>
		</div>
	);
});

export default Blog;
